import { LogoutLinkDto } from 'libs/end-to-end/src/auth';
import { Result } from 'libs/end-to-end/src/result';
import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';
import { errResult } from 'libs/end-to-end/src/result';
import { okResult } from 'libs/end-to-end/src/result';
import { responseToJson } from 'libs/end-to-end/src/http-helpers';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { createZodParseError } from 'libs/end-to-end/src/utils/utility/create-zod-parse-error';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { ClientLoginError } from 'apps/sigma2-ui/src/lib/client-login-error';
import { EmptyObject } from 'libs/end-to-end/src/generic';

export interface ApiCallOptions<TResponse> {
  responseSchema: S2SafeParsable<unknown, TResponse>;
  url: string;
  requestOptions?: RequestInit;
  deserializeErrorFn: (input: unknown) => Result<BaseError, BaseError>;
}

export const loginWithEmailAndPassword = async (
  email: string,
  password: string,
): Promise<Result<EmptyObject, ClientLoginError>> => {
  let response: Response;
  try {
    response = await fetch(getApiBaseUrl() + '/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });
  } catch (e) {
    return errResult({
      errorType: 'UnknownError',
      errorReason: e instanceof Error ? e.message : 'Unknown error',
    });
  }
  if (response.status >= 400) {
    return errResult({
      errorType: 'InvalidCredentialsError',
    });
  }

  return okResult({});
};

export const loginWithSSOCode = async (
  code: string,
  redirectUri: string,
): Promise<Result<EmptyObject, ClientLoginError>> => {
  let response: Response;
  try {
    response = await fetch(getApiBaseUrl() + '/auth/login/sso', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code: code, redirectUri }),
      credentials: 'include',
    });
  } catch (e) {
    return errResult({
      errorType: 'UnknownError',
      errorReason: e instanceof Error ? e.message : 'Unknown error',
    });
  }
  if (response.status >= 400) {
    return errResult({
      errorType: 'InvalidCredentialsError',
    });
  }

  return okResult({});
};

export const generateLogoutLink = async (
  logoutUri: string,
): Promise<Result<string, ClientLoginError>> => {
  let response: Response;
  try {
    response = await fetch(getApiBaseUrl() + '/auth/logout-link/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ logoutUri }),
    });
  } catch (e) {
    return errResult({
      errorType: 'UnknownError',
      errorReason: e instanceof Error ? e.message : 'Unknown error',
    });
  }
  if (response.status >= 400) {
    return errResult({
      errorType: 'InvalidCredentialsError',
    });
  }

  const responseToJsonResultWithMeta = await responseToJson({
    response,
    schema: LogoutLinkDto,
  });

  if (responseToJsonResultWithMeta.value.resultType === 'err') {
    return errResult(responseToJsonResultWithMeta.value.error);
  }

  return okResult(responseToJsonResultWithMeta.value.value.link);
};

export const apiLogout = async (): Promise<
  Result<EmptyObject, ClientLoginError>
> => {
  try {
    await fetch(`${getApiBaseUrl()}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
  } catch (e) {
    return errResult({
      errorType: 'UnknownError',
      errorReason: e instanceof Error ? e.message : 'Unknown error',
    });
  }

  return okResult({});
};

// TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
export const authenticatedApiCall = async <TResponse>(
  options: ApiCallOptions<TResponse>,
): Promise<Result<TResponse, BaseError>> => {
  const { url, requestOptions, deserializeErrorFn } = options;

  const response = await fetch(url, {
    ...(requestOptions || {}),
    headers: {
      'Content-Type': 'application/json',
      ...(requestOptions?.headers || {}),
    },
    credentials: 'include',
  });

  if (response.status === 401) {
    // In all endpoints, a 401 indicates that we should logout, so let's override the result meta.
    return errResult({
      errorType: 'InvalidCredentialsError',
      errorReason: 'Invalid credentials',
    });
  }

  if (response.status >= 400) {
    // Most endpoints will simply return an ApiError
    const deserializeErrorResult = deserializeErrorFn(await response.json());
    const deserializeError =
      deserializeErrorResult.resultType === 'ok'
        ? deserializeErrorResult.value
        : deserializeErrorResult.error;

    return errResult(deserializeError);
  }

  const responseToJsonResultWithMeta = await responseToJson({
    response,
    schema: options.responseSchema,
  });

  const { value: responseToJsonResult, meta: resultAny } =
    responseToJsonResultWithMeta;

  if (responseToJsonResult.resultType === 'err') {
    const deserializeErrorResult = deserializeErrorFn(null);
    const deserializeError =
      deserializeErrorResult.resultType === 'ok'
        ? deserializeErrorResult.value
        : deserializeErrorResult.error;

    return errResult(deserializeError);
  }

  const parseResult = options.responseSchema.safeParse(resultAny);

  if (!parseResult.success) {
    const deserializeErrorResult = deserializeErrorFn(resultAny);
    const deserializeError =
      deserializeErrorResult.resultType === 'ok'
        ? deserializeErrorResult.value
        : deserializeErrorResult.error;
    if (deserializeErrorResult.resultType === 'err') {
      return errResult(createZodParseError(parseResult));
    }
    return errResult(deserializeError);
  }

  return okResult(parseResult.data);
};

export const apiCall = async <TResponse>(
  options: ApiCallOptions<TResponse>,
): Promise<Result<TResponse, BaseError>> => {
  const { url, requestOptions, deserializeErrorFn } = options;

  const response = await fetch(url, {
    ...(requestOptions || {}),
    headers: {
      'Content-Type': 'application/json',
      ...(requestOptions?.headers || {}),
    },
  });

  if (response.status >= 400) {
    const deserializeErrorResult = deserializeErrorFn(await response.json());
    const deserializeError =
      deserializeErrorResult.resultType === 'ok'
        ? deserializeErrorResult.value
        : deserializeErrorResult.error;

    return errResult(deserializeError);
  }

  const responseToJsonResultWithMeta = await responseToJson({
    response,
    schema: options.responseSchema,
  });

  const { value: responseToJsonResult, meta: resultAny } =
    responseToJsonResultWithMeta;

  if (responseToJsonResult.resultType === 'err') {
    const deserializeErrorResult = deserializeErrorFn(resultAny);
    const deserializeError =
      deserializeErrorResult.resultType === 'ok'
        ? deserializeErrorResult.value
        : deserializeErrorResult.error;

    return errResult(deserializeError);
  }

  return okResult(responseToJsonResult.value);
};
